import React from 'react';

interface Props {
  children: React.ReactNode;
}

interface State {
  error: Error | null;
  info: React.ErrorInfo | null;
}

/**
 * Catches anything thrown while rendering the app and shows it on screen.
 *
 * Without this, a single throw in a provider or layout unmounts the whole tree and React
 * renders nothing — the user gets a blank white page and the only trace is in the browser
 * console, which is exactly the situation this was added to end. A crash should say what
 * it was.
 *
 * Only render errors reach here. Errors inside event handlers, promise rejections and
 * async callbacks are NOT caught by error boundaries — those still surface in the console.
 */
class RootErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null, info: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    this.setState({ error, info });
    // eslint-disable-next-line no-console
    console.error('[RootErrorBoundary] app crashed while rendering:', error, info?.componentStack);
  }

  render() {
    const { error, info } = this.state;
    if (!error) return <>{this.props.children}</>;

    return (
      <div style={{ minHeight: '100vh', padding: '2rem', background: '#f7f9f8', fontFamily: 'system-ui, sans-serif' }}>
        <div
          style={{
            maxWidth: 900,
            margin: '0 auto',
            background: '#fff',
            border: '1px solid #dbe5e1',
            borderLeft: '4px solid #c0392b',
            borderRadius: 12,
            padding: '1.75rem',
          }}
        >
          <h2 style={{ margin: '0 0 .5rem', color: '#101413', fontSize: 20 }}>Something went wrong</h2>
          <p style={{ color: '#5b6b66', marginBottom: '1.25rem', fontSize: 14 }}>
            The page failed to render. The details below are what the browser console would show.
          </p>

          <pre
            style={{
              background: '#fbf3f2',
              border: '1px solid #f0d6d2',
              borderRadius: 8,
              padding: '1rem',
              fontSize: 12.5,
              lineHeight: 1.5,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              color: '#7d2f24',
              margin: 0,
            }}
          >
            {String(error?.stack || error?.message || error)}
            {info?.componentStack ? `\n\nComponent stack:${info.componentStack}` : ''}
          </pre>

          <button
            type="button"
            onClick={() => window.location.reload()}
            style={{
              marginTop: '1.25rem',
              background: '#1f7c50',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              padding: '.6rem 1.4rem',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Reload
          </button>
        </div>
      </div>
    );
  }
}

export default RootErrorBoundary;
